<template>
  <div class="dark-theme-wrapper">
    <div class="container py-5">
      <h1 class="main-title text-center mb-5">VYHODNOCENÍ KOLA</h1>

      <div class="round-info text-center mb-5">
        <h2 class="section-title">
          PRÁVĚ VYHODNOCUJETE:
          <span class="text-red">{{ roundNumber }}. KOLO</span>
        </h2>
      </div>

      <div v-if="loading" class="text-center text-muted">
        <div class="spinner-border text-danger" role="status"></div>
        <p class="mt-2">Načítám data...</p>
      </div>

      <div v-else>
        <div v-if="matches.length">
          <div v-for="match in matches" :key="match.id" class="admin-card mb-4">
            <div class="card-header-custom">
              <span class="match-id">ID: {{ match.id.slice(0, 4) }}...</span>
              <span v-if="match.evaluated" class="badge-evaluated"
                >JIŽ VYHODNOCENO</span
              >
              <span v-else class="badge-pending">ČEKÁ NA VÝSLEDEK</span>
            </div>

            <div class="match-grid">
              <div class="team-col">
                <img :src="match.homeLogo" alt="Home" class="team-logo" />
                <span class="team-name">{{ match.homeTeam }}</span>
              </div>

              <div class="score-inputs">
                <input
                  type="number"
                  v-model.number="match.finalHomeScore"
                  class="score-input"
                  min="0"
                  placeholder="-"
                />
                <span class="divider">:</span>
                <input
                  type="number"
                  v-model.number="match.finalAwayScore"
                  class="score-input"
                  min="0"
                  placeholder="-"
                />
              </div>

              <div class="team-col">
                <img :src="match.awayLogo" alt="Away" class="team-logo" />
                <span class="team-name">{{ match.awayTeam }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="alert-box warning mb-5 d-flex align-items-center justify-content-center"
        >
          <span>Pro toto kolo nejsou v systému žádné zápasy.</span>
        </div>

        <div v-if="question" class="admin-card bonus-card mb-5">
          <h3 class="bonus-title d-flex align-items-center">
            DOPLŇUJÍCÍ OTÁZKA
          </h3>
          <p class="question-text mb-4">{{ question }}</p>

          <div class="radio-group">
            <label
              class="custom-radio"
              :class="{ selected: correctAnswer === 'Ano' }"
            >
              <input type="radio" value="Ano" v-model="correctAnswer" />
              <span>ANO</span>
            </label>

            <label
              class="custom-radio"
              :class="{ selected: correctAnswer === 'Ne' }"
            >
              <input type="radio" value="Ne" v-model="correctAnswer" />
              <span>NE</span>
            </label>
          </div>

          <div class="text-center mt-3">
            <button class="btn-reset" @click="correctAnswer = ''">
              Vymazat odpověď
            </button>
          </div>
        </div>

        <div class="action-bar">
          <p class="text-muted small mb-3">
            ⚠️ Kliknutím se uloží výsledky a přepočítají body všem hráčům podle
            pravidel (10/7/3 bodů). <br />
            Pokud již body mají, systém je aktualizuje.
          </p>
          <button
            class="btn-red-glow action-btn"
            @click="submitResults"
            :disabled="matches.length === 0 || processing"
          >
            <span
              v-if="processing"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ processing ? "PŘEPOČÍTÁVÁM..." : "ULOŽIT A VYHODNOTIT KOLO" }}
          </button>
        </div>

        <transition name="fade">
          <div
            v-if="successMessage"
            class="alert-box success mt-4 d-flex align-items-center justify-content-center"
          >
            ✅ <span>{{ successMessage }}</span>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "../auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
  query,
  where,
} from "firebase/firestore";

export default {
  name: "AdminResult",
  data() {
    return {
      roundNumber: null,
      matches: [],
      question: "",
      correctAnswer: "",
      successMessage: "",
      loading: true,
      processing: false,
    };
  },
  mounted() {
    this.fetchVisibleRound();
  },
  methods: {
    async fetchVisibleRound() {
      try {
        const roundRef = doc(db, "settings", "visibleRound");
        const roundSnap = await getDoc(roundRef);
        if (roundSnap.exists()) {
          this.roundNumber = roundSnap.data().roundNumber;
        } else {
          this.roundNumber = 1;
        }
        await this.fetchMatches();
        await this.fetchQuestion();
      } catch (error) {
        console.error("Chyba:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchMatches() {
      try {
        const matchesQuery = query(
          collection(db, "matches"),
          where("roundNumber", "==", this.roundNumber)
        );
        const querySnapshot = await getDocs(matchesQuery);

        if (!querySnapshot.empty) {
          this.matches = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            finalHomeScore: doc.data().finalHomeScore ?? null,
            finalAwayScore: doc.data().finalAwayScore ?? null,
          }));
        } else {
          this.matches = [];
        }
      } catch (error) {
        console.error("Chyba:", error);
      }
    },

    async fetchQuestion() {
      try {
        // Hledáme v questions podle ID kola
        const questionRef = doc(db, "questions", String(this.roundNumber));
        const questionDoc = await getDoc(questionRef);

        if (questionDoc.exists()) {
          const data = questionDoc.data();
          this.question = data.question;
          this.correctAnswer = data.correctAnswer || "";
        }
      } catch (error) {
        console.error("Chyba otázky:", error);
      }
    },

    async submitResults() {
      if (
        !confirm("Opravdu chcete uložit výsledky a přepsat body všem hráčům?")
      )
        return;

      this.processing = true;
      this.successMessage = "";

      try {
        const batch = writeBatch(db);

        // 1. Uložení výsledků zápasů
        this.matches.forEach((match) => {
          if (match.finalHomeScore !== null && match.finalAwayScore !== null) {
            const matchRef = doc(db, "matches", match.id);
            batch.update(matchRef, {
              finalHomeScore: match.finalHomeScore,
              finalAwayScore: match.finalAwayScore,
              evaluated: true,
            });
          }
        });

        // 2. Uložení odpovědi na otázku
        if (this.question) {
          const qRef = doc(db, "questions", String(this.roundNumber));
          batch.update(qRef, {
            correctAnswer: this.correctAnswer,
            evaluated: !!this.correctAnswer,
          });
        }

        await batch.commit();

        // 3. PŘEPOČET BODŮ podle nových pravidel
        await this.calculatePointsForUsers();

        this.successMessage = "Hotovo! Výsledky uloženy a body rozděleny.";
        await this.fetchMatches();
      } catch (error) {
        console.error("Chyba při ukládání:", error);
        alert("Chyba: " + error.message);
      } finally {
        this.processing = false;
      }
    },

    async calculatePointsForUsers() {
      console.log("Začínám přepočet bodů (pravidla 10/7/3)...");

      const tipsQuery = query(
        collection(db, "tips"),
        where("roundNumber", "==", this.roundNumber)
      );
      const tipsSnap = await getDocs(tipsQuery);

      const usersSnap = await getDocs(collection(db, "users"));
      const usersMap = {};
      usersSnap.forEach((u) => {
        usersMap[u.id] = { ref: u.ref, data: u.data() };
      });

      const batch = writeBatch(db);
      let updatesCount = 0;

      tipsSnap.forEach((tipDoc) => {
        const tipData = tipDoc.data();
        const userId = tipData.user;

        if (!usersMap[userId]) return;

        let roundPoints = 0;

        // --- VYHODNOCENÍ ZÁPASŮ ---
        this.matches.forEach((match) => {
          if (
            match.finalHomeScore === null ||
            match.finalHomeScore === "" ||
            match.finalAwayScore === null ||
            match.finalAwayScore === ""
          )
            return;

          const userTip = tipData.tips[match.id];
          if (userTip) {
            const mHome = Number(match.finalHomeScore);
            const mAway = Number(match.finalAwayScore);
            const tHome = Number(userTip.homeScore);
            const tAway = Number(userTip.awayScore);

            // 1. PŘESNÝ VÝSLEDEK = 10 bodů
            if (tHome === mHome && tAway === mAway) {
              roundPoints += 10;
            } else {
              // Zjistíme, jestli uhodl vítěze/remízu
              const realSign = Math.sign(mHome - mAway);
              const tipSign = Math.sign(tHome - tAway);

              if (realSign === tipSign) {
                // --- NOVINKA: Zvýhodněná remíza ---
                if (realSign === 0) {
                  // Uhodl remízu (ale ne přesné skóre), bere solidních 7 bodů
                  roundPoints += 7;
                }
                // 2. SPRÁVNÝ VÍTĚZ + GÓLY JEDNOHO TÝMU = 7 bodů (pro výhry/prohry)
                else if (tHome === mHome || tAway === mAway) {
                  roundPoints += 7;
                }
                // 3. JEN SPRÁVNÝ VÍTĚZ = 3 body
                else {
                  roundPoints += 3;
                }
              }
              // Jinak 0 bodů
            }
          }
        });

        // --- OTÁZKA (+5 bodů) ---
        // Nejdříve přičteme body za správnou otázku
        if (
          this.correctAnswer &&
          tipData.questionAnswer === this.correctAnswer
        ) {
          roundPoints += 5;
        }

        // --- JOKER (2x) ---
        // Až POTOM vynásobíme Jokerem úplně vše (zápasy i otázku)
        if (tipData.useJoker) {
          roundPoints *= 2;
        }

        // --- AKTUALIZACE UŽIVATELE ---
        const userObj = usersMap[userId];
        const userData = userObj.data;

        const oldRoundPoints =
          userData.rounds && userData.rounds[this.roundNumber]
            ? userData.rounds[this.roundNumber]
            : 0;
        const currentTotal = userData.totalPoints || 0;

        // Nový celkový součet = (Aktuální celkem - Staré za toto kolo) + Nové za toto kolo
        const newTotal = currentTotal - oldRoundPoints + roundPoints;

        const updateData = {
          totalPoints: newTotal,
          [`rounds.${this.roundNumber}`]: roundPoints,
        };

        batch.update(userObj.ref, updateData);
        batch.update(tipDoc.ref, { points: roundPoints }); // Uložíme body i do tipu

        updatesCount++;
      });

      if (updatesCount > 0) {
        await batch.commit();
        console.log(`Přepočítáno ${updatesCount} hráčů.`);
      }
    },
  },
};
</script>

<style scoped>
/* Styly stejné jako minule */
.dark-theme-wrapper {
  background-color: #000000;
  min-height: 100vh;
  color: #ffffff;
  font-family: "Roboto", sans-serif;
}
.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #fff;
  text-transform: uppercase;
}
.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ccc;
  letter-spacing: 1px;
}
.text-red {
  color: #dc3545;
}
.admin-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}
.card-header-custom {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  font-size: 0.8rem;
  color: #666;
}
.badge-evaluated {
  color: #28a745;
  font-weight: bold;
}
.badge-pending {
  color: #ffc107;
  font-weight: bold;
}
.match-grid {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}
.team-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30%;
  text-align: center;
}
.team-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  margin-bottom: 5px;
}
.team-name {
  font-weight: bold;
  font-size: 0.9rem;
  line-height: 1.2;
}
.score-inputs {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.score-input {
  width: 60px;
  height: 60px;
  background: #000;
  border: 2px solid #444;
  color: #fff;
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  border-radius: 8px;
}
.score-input:focus {
  border-color: #dc3545;
  outline: none;
}
.score-input:disabled {
  opacity: 0.5;
  border-color: #222;
  cursor: not-allowed;
}
.divider {
  font-size: 2rem;
  font-weight: bold;
  color: #666;
}
.bonus-card {
  border-left: 4px solid #dc3545;
}
.bonus-title {
  color: #dc3545;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.question-text {
  font-size: 1.1rem;
  font-style: italic;
}
.radio-group {
  display: flex;
  justify-content: center;
  gap: 20px;
}
.custom-radio {
  background: #222;
  border: 1px solid #444;
  padding: 10px 30px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}
.custom-radio input {
  display: none;
}
.custom-radio.selected {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
  box-shadow: 0 0 10px rgba(220, 53, 69, 0.5);
}
.action-bar {
  text-align: center;
  margin-top: 40px;
  padding-bottom: 40px;
}
.btn-red-glow {
  background: linear-gradient(135deg, #a71d2a 0%, #dc3545 100%);
  color: white;
  padding: 15px 40px;
  border: none;
  border-radius: 30px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
  font-size: 1.2rem;
}
.btn-red-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.6);
}
.btn-red-glow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #333;
  box-shadow: none;
}
.btn-reset {
  background: none;
  border: none;
  color: #666;
  font-size: 0.8rem;
  text-decoration: underline;
  cursor: pointer;
}
.btn-reset:hover {
  color: #fff;
}
.alert-box {
  padding: 15px;
  border-radius: 8px;
  font-weight: bold;
}
.alert-box.success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid #28a745;
}
.alert-box.warning {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid #ffc107;
  text-align: center;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
